import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  FormControlLabel,
  Checkbox,
  Alert,
  Card,
  CardContent,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Slider,
  IconButton,
  Divider,
  Stack,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  CheckCircle as CompleteIcon,
  Timeline as TimelineIcon,
  Flag as MilestoneIcon,
  TrendingFlat as ArrowIcon,
  AccountTree as PertIcon,
  CalendarMonth as GanttIcon,
  PlayArrow as StartIcon,
  Stop as EndIcon,
  Inventory as DeliverableIcon,
  CheckCircleOutline as CheckpointIcon,
  HelpOutline as DecisionIcon,
  Link as DependencyIcon,
  Schedule as BufferIcon
} from '@mui/icons-material';

import { useTranslation } from 'react-i18next';
import { format, differenceInDays, addDays, parseISO } from 'date-fns';

import useRoadmapStore from '../store/roadmapStore';
import { STEP_LABELS } from '../utils/constants';
import StepPageHeader from '../components/StepPageHeader';
import useAISuggest from '../hooks/useAISuggest';

// Color palette for phases
const PHASE_COLORS = [
  '#1976d2', '#2e7d32', '#ed6c02', '#9c27b0', 
  '#d32f2f', '#0288d1', '#7b1fa2', '#c62828'
];

const STATUS_CONFIG = {
  'not-started': { label: 'Not Started', color: 'default' },
  'in-progress': { label: 'In Progress', color: 'primary' },
  'completed': { label: 'Completed', color: 'success' },
  'delayed': { label: 'Delayed', color: 'error' },
  'on-hold': { label: 'On Hold', color: 'warning' }
};

const MILESTONE_ICONS = {
  'start': <StartIcon fontSize="small" />,
  'end': <EndIcon fontSize="small" />,
  'deliverable': <DeliverableIcon fontSize="small" />,
  'checkpoint': <CheckpointIcon fontSize="small" />,
  'decision': <DecisionIcon fontSize="small" />
};

// Gantt Chart Component
const GanttChart = ({ phases, startDate, endDate, onPhaseClick }) => {
  // Parse dates and calculate values before any conditional returns
  const projectStart = startDate ? parseISO(startDate) : null;
  const projectEnd = endDate ? parseISO(endDate) : null;
  const totalDays = projectStart && projectEnd ? differenceInDays(projectEnd, projectStart) : 0;

  // Generate month markers (must be before conditional return)
  const months = useMemo(() => {
    if (!projectStart || !projectEnd || totalDays <= 0) return [];
    
    const markers = [];
    let currentDate = projectStart;
    
    while (currentDate <= projectEnd) {
      const daysFromStart = differenceInDays(currentDate, projectStart);
      markers.push({
        date: currentDate,
        position: (daysFromStart / totalDays) * 100,
        label: format(currentDate, 'MMM yyyy')
      });
      currentDate = addDays(currentDate, 30);
    }
    
    return markers;
  }, [projectStart, projectEnd, totalDays]);

  const getPhasePosition = (phase) => {
    if (!phase.start_date || !phase.end_date || !projectStart || totalDays <= 0) return null;
    
    const phaseStart = parseISO(phase.start_date);
    const phaseEnd = parseISO(phase.end_date);
    
    const startOffset = differenceInDays(phaseStart, projectStart);
    const duration = differenceInDays(phaseEnd, phaseStart);
    
    return {
      left: (startOffset / totalDays) * 100,
      width: (duration / totalDays) * 100,
      startOffset,
      duration
    };
  };

  // Early return after all hooks
  if (!startDate || !endDate) {
    return (
      <Alert severity="info">
        Please set project start and end dates first
      </Alert>
    );
  }

  return (
    <Box sx={{ width: '100%', overflowX: 'auto', pb: 2 }}>
      {/* Timeline header with months */}
      <Box sx={{ position: 'relative', height: 40, mb: 2, borderBottom: 2, borderColor: 'divider' }}>
        {months.map((month, idx) => (
          <Box
            key={idx}
            sx={{
              position: 'absolute',
              left: `${month.position}%`,
              transform: 'translateX(-50%)',
              textAlign: 'center'
            }}
          >
            <Typography variant="caption" fontWeight={600}>
              {month.label}
            </Typography>
            <Box sx={{ width: 1, height: 10, bgcolor: 'divider', mx: 'auto' }} />
          </Box>
        ))}
      </Box>

      {/* Phases */}
      <Stack spacing={2}>
        {phases.map((phase, index) => {
          const position = getPhasePosition(phase);
          
          if (!position) {
            return (
              <Card key={index} variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
                <Typography variant="body2">
                  {phase.name} - <em>Dates not set</em>
                </Typography>
              </Card>
            );
          }

          const statusConfig = STATUS_CONFIG[phase.status] || STATUS_CONFIG['not-started'];
          const color = phase.color || PHASE_COLORS[index % PHASE_COLORS.length];

          return (
            <Box key={index}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                <Typography variant="body2" sx={{ minWidth: 150, fontWeight: 600 }}>
                  {phase.name}
                </Typography>
                <Chip
                  label={statusConfig.label}
                  color={statusConfig.color}
                  size="small"
                  sx={{ minWidth: 100 }}
                />
                {phase.is_critical && (
                  <Chip label="Critical" color="error" size="small" variant="outlined" />
                )}
                {phase.dependencies && phase.dependencies.length > 0 && (
                  <Tooltip title={`Depends on: Phase ${phase.dependencies.map(d => d + 1).join(', ')}`}>
                    <Chip icon={<DependencyIcon />} label={phase.dependencies.length} size="small" />
                  </Tooltip>
                )}
              </Box>

              <Box sx={{ position: 'relative', height: 60, bgcolor: 'grey.100', borderRadius: 1, overflow: 'visible' }}>
                {/* Baseline (if different from actual) */}
                {phase.baseline_start && phase.baseline_end && 
                 (phase.baseline_start !== phase.start_date || phase.baseline_end !== phase.end_date) && (
                  <Box
                    sx={{
                      position: 'absolute',
                      left: `${((differenceInDays(parseISO(phase.baseline_start), projectStart)) / totalDays) * 100}%`,
                      width: `${((differenceInDays(parseISO(phase.baseline_end), parseISO(phase.baseline_start))) / totalDays) * 100}%`,
                      top: 8,
                      height: 6,
                      bgcolor: 'grey.400',
                      opacity: 0.5,
                      borderRadius: 1
                    }}
                  />
                )}

                {/* Actual phase bar */}
                <Tooltip
                  title={
                    <Box>
                      <Typography variant="caption" display="block" fontWeight="bold">
                        {phase.name}
                      </Typography>
                      <Typography variant="caption" display="block">
                        {phase.start_date} to {phase.end_date}
                      </Typography>
                      <Typography variant="caption" display="block">
                        Duration: {position.duration} days
                      </Typography>
                      <Typography variant="caption" display="block">
                        Progress: {phase.progress || 0}%
                      </Typography>
                      {phase.assigned_to && (
                        <Typography variant="caption" display="block">
                          Assigned: {phase.assigned_to}
                        </Typography>
                      )}
                    </Box>
                  }
                >
                  <Box
                    onClick={() => onPhaseClick(index)}
                    sx={{
                      position: 'absolute',
                      left: `${position.left}%`,
                      width: `${position.width}%`,
                      top: 20,
                      height: 24,
                      bgcolor: color,
                      borderRadius: 1,
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      '&:hover': {
                        transform: 'scaleY(1.1)',
                        boxShadow: 3
                      }
                    }}
                  >
                    {/* Progress indicator */}
                    <Box
                      sx={{
                        position: 'absolute',
                        left: 0,
                        top: 0,
                        width: `${phase.progress || 0}%`,
                        height: '100%',
                        bgcolor: 'rgba(255, 255, 255, 0.3)',
                        borderRadius: 1
                      }}
                    />

                    {/* Phase label */}
                    <Typography
                      variant="caption"
                      sx={{
                        position: 'absolute',
                        left: '50%',
                        top: '50%',
                        transform: 'translate(-50%, -50%)',
                        color: 'white',
                        fontWeight: 600,
                        whiteSpace: 'nowrap',
                        textShadow: '1px 1px 2px rgba(0,0,0,0.3)'
                      }}
                    >
                      {phase.progress || 0}%
                    </Typography>
                  </Box>
                </Tooltip>

                {/* Milestones */}
                {phase.milestones && phase.milestones.map((milestone, mIdx) => {
                  if (!milestone.date) return null;
                  
                  const milestoneDate = parseISO(milestone.date);
                  const milestonePos = (differenceInDays(milestoneDate, projectStart) / totalDays) * 100;
                  
                  return (
                    <Tooltip key={mIdx} title={`${milestone.name} - ${milestone.date}`}>
                      <Box
                        sx={{
                          position: 'absolute',
                          left: `${milestonePos}%`,
                          top: 0,
                          transform: 'translateX(-50%)',
                          zIndex: 10
                        }}
                      >
                        <Box
                          sx={{
                            width: 0,
                            height: 0,
                            borderLeft: '8px solid transparent',
                            borderRight: '8px solid transparent',
                            borderTop: milestone.completed ? '12px solid #2e7d32' : '12px solid #ff9800',
                            cursor: 'pointer'
                          }}
                        />
                      </Box>
                    </Tooltip>
                  );
                })}
              </Box>

              {/* Dependencies arrows (simplified) */}
              {phase.dependencies && phase.dependencies.length > 0 && position && (
                <Box sx={{ mt: 0.5, ml: 2 }}>
                  <Typography variant="caption" color="text.secondary">
                    <ArrowIcon sx={{ fontSize: 12, verticalAlign: 'middle' }} />
                    {' '}Depends on Phase {phase.dependencies.map(d => d + 1).join(', ')}
                  </Typography>
                </Box>
              )}
            </Box>
          );
        })}
      </Stack>
    </Box>
  );
};

// PERT Chart Component (Network Diagram)
const PertChart = ({ phases }) => {
  // Calculate critical path
  const criticalPath = useMemo(() => {
    return phases.filter(p => p.is_critical).map(p => p.name);
  }, [phases]);

  if (phases.length === 0) {
    return (
      <Alert severity="info">
        Add phases to see the PERT network diagram
      </Alert>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        PERT Network Diagram
      </Typography>
      
      {criticalPath.length > 0 && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Critical Path: {criticalPath.join(' → ')}
        </Alert>
      )}

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, justifyContent: 'center', alignItems: 'center' }}>
        {phases.map((phase, index) => (
          <React.Fragment key={index}>
            <Card
              elevation={phase.is_critical ? 8 : 2}
              sx={{
                minWidth: 180,
                border: phase.is_critical ? 3 : 1,
                borderColor: phase.is_critical ? 'error.main' : 'divider',
                bgcolor: phase.is_critical ? 'error.50' : 'background.paper'
              }}
            >
              <CardContent>
                <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                  Phase {index + 1}
                </Typography>
                <Typography variant="body2" gutterBottom>
                  {phase.name}
                </Typography>
                
                <Divider sx={{ my: 1 }} />
                
                <Typography variant="caption" display="block">
                  Duration: {phase.start_date && phase.end_date 
                    ? `${differenceInDays(parseISO(phase.end_date), parseISO(phase.start_date))} days`
                    : 'N/A'}
                </Typography>
                
                {phase.buffer_time > 0 && (
                  <Typography variant="caption" display="block" color="warning.main">
                    <BufferIcon sx={{ fontSize: 12 }} /> Buffer: {phase.buffer_time}d
                  </Typography>
                )}
                
                {phase.dependencies && phase.dependencies.length > 0 && (
                  <Chip
                    label={`Deps: ${phase.dependencies.length}`}
                    size="small"
                    sx={{ mt: 1 }}
                  />
                )}
              </CardContent>
            </Card>
            
            {index < phases.length - 1 && (
              <ArrowIcon sx={{ color: 'text.secondary', fontSize: 32 }} />
            )}
          </React.Fragment>
        ))}
      </Box>
    </Box>
  );
};

// Phase Form Dialog
const PhaseForm = ({ phase, phaseIndex, phases, onSave, onCancel }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: phase?.name || '',
    description: phase?.description || '',
    start_date: phase?.start_date || '',
    end_date: phase?.end_date || '',
    baseline_start: phase?.baseline_start || '',
    baseline_end: phase?.baseline_end || '',
    status: phase?.status || 'not-started',
    progress: phase?.progress || 0,
    buffer_time: phase?.buffer_time || 0,
    is_critical: phase?.is_critical || false,
    dependencies: phase?.dependencies || [],
    assigned_to: phase?.assigned_to || '',
    color: phase?.color || PHASE_COLORS[phaseIndex % PHASE_COLORS.length],
    milestones: phase?.milestones || []
  });

  const [newMilestone, setNewMilestone] = useState({
    name: '',
    date: '',
    description: '',
    completed: false,
    type: 'checkpoint'
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (formData.name.trim()) {
      onSave(formData);
    }
  };

  const handleChange = (field) => (e) => {
    setFormData({ ...formData, [field]: e.target.value });
  };

  const addMilestone = () => {
    if (newMilestone.name.trim() && newMilestone.date) {
      setFormData({
        ...formData,
        milestones: [...formData.milestones, newMilestone]
      });
      setNewMilestone({
        name: '',
        date: '',
        description: '',
        completed: false,
        type: 'checkpoint'
      });
    }
  };

  const deleteMilestone = (index) => {
    setFormData({
      ...formData,
      milestones: formData.milestones.filter((_, i) => i !== index)
    });
  };

  const toggleMilestoneComplete = (index) => {
    const updated = formData.milestones.map((m, i) =>
      i === index ? { ...m, completed: !m.completed } : m
    );
    setFormData({ ...formData, milestones: updated });
  };

  // Available phases for dependencies (exclude current phase)
  const availablePhases = phases
    .map((p, idx) => ({ ...p, index: idx }))
    .filter((_, idx) => idx !== phaseIndex);

  return (
    <Paper elevation={3} sx={{ p: 3, maxHeight: '80vh', overflow: 'auto' }}>
      <Typography variant="h5" gutterBottom>
        {phase ? 'Edit Phase' : 'Add New Phase'}
      </Typography>
      
      <form onSubmit={handleSubmit}>
        <Grid container spacing={2}>
          {/* Basic Information */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
              Basic Information
            </Typography>
          </Grid>

          <Grid item xs={12} md={8}>
            <TextField
              fullWidth
              label="Phase Name"
              value={formData.name}
              onChange={handleChange('name')}
              required
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={formData.status}
                onChange={handleChange('status')}
                label="Status"
              >
                {Object.entries(STATUS_CONFIG).map(([key, config]) => (
                  <MenuItem key={key} value={key}>
                    {config.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Description"
              multiline
              rows={3}
              value={formData.description}
              onChange={handleChange('description')}
            />
          </Grid>

          {/* Dates */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
              Schedule
            </Typography>
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Start Date"
              type="date"
              value={formData.start_date}
              onChange={handleChange('start_date')}
              InputLabelProps={{ shrink: true }}
              required
            />
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="End Date"
              type="date"
              value={formData.end_date}
              onChange={handleChange('end_date')}
              InputLabelProps={{ shrink: true }}
              required
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Baseline Start (Original Plan)"
              type="date"
              value={formData.baseline_start}
              onChange={handleChange('baseline_start')}
              InputLabelProps={{ shrink: true }}
              helperText="Original planned start date"
            />
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Baseline End (Original Plan)"
              type="date"
              value={formData.baseline_end}
              onChange={handleChange('baseline_end')}
              InputLabelProps={{ shrink: true }}
              helperText="Original planned end date"
            />
          </Grid>

          {/* Progress and Settings */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
              Progress & Settings
            </Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography gutterBottom>
              Progress: {formData.progress}%
            </Typography>
            <Slider
              value={formData.progress}
              onChange={(e, value) => setFormData({ ...formData, progress: value })}
              min={0}
              max={100}
              step={5}
              marks={[
                { value: 0, label: '0%' },
                { value: 50, label: '50%' },
                { value: 100, label: '100%' }
              ]}
              valueLabelDisplay="auto"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Buffer Time (days)"
              type="number"
              value={formData.buffer_time}
              onChange={handleChange('buffer_time')}
              inputProps={{ min: 0 }}
              helperText="Extra time buffer for this phase"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Assigned To"
              value={formData.assigned_to}
              onChange={handleChange('assigned_to')}
              placeholder="Team or person responsible"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Phase Color"
              type="color"
              value={formData.color}
              onChange={handleChange('color')}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.is_critical}
                  onChange={(e) => setFormData({ ...formData, is_critical: e.target.checked })}
                />
              }
              label="Mark as Critical Path"
            />
          </Grid>

          {/* Dependencies */}
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel>Dependencies</InputLabel>
              <Select
                multiple
                value={formData.dependencies}
                onChange={(e) => setFormData({ ...formData, dependencies: e.target.value })}
                label="Dependencies"
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((value) => (
                      <Chip key={value} label={`Phase ${value + 1}`} size="small" />
                    ))}
                  </Box>
                )}
              >
                {availablePhases.map((p) => (
                  <MenuItem key={p.index} value={p.index}>
                    Phase {p.index + 1}: {p.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Milestones */}
          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
            <Typography variant="h6" gutterBottom>
              Milestones
            </Typography>
            
            <Card variant="outlined" sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
              <Grid container spacing={1} alignItems="center">
                <Grid item xs={12} sm={3}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Milestone Name"
                    value={newMilestone.name}
                    onChange={(e) => setNewMilestone({ ...newMilestone, name: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12} sm={2}>
                  <TextField
                    fullWidth
                    size="small"
                    type="date"
                    label="Date"
                    value={newMilestone.date}
                    onChange={(e) => setNewMilestone({ ...newMilestone, date: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} sm={2}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Type</InputLabel>
                    <Select
                      value={newMilestone.type}
                      onChange={(e) => setNewMilestone({ ...newMilestone, type: e.target.value })}
                      label="Type"
                    >
                      <MenuItem value="start">Start</MenuItem>
                      <MenuItem value="end">End</MenuItem>
                      <MenuItem value="deliverable">Deliverable</MenuItem>
                      <MenuItem value="checkpoint">Checkpoint</MenuItem>
                      <MenuItem value="decision">Decision</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Description"
                    value={newMilestone.description}
                    onChange={(e) => setNewMilestone({ ...newMilestone, description: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12} sm={1}>
                  <Button 
                    fullWidth
                    variant="contained"
                    size="small"
                    onClick={addMilestone}
                    disabled={!newMilestone.name.trim() || !newMilestone.date}
                  >
                    <AddIcon />
                  </Button>
                </Grid>
              </Grid>
            </Card>
            
            <Stack spacing={1}>
              {formData.milestones.map((milestone, index) => (
                <Card key={index} variant="outlined" sx={{ p: 1.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <IconButton
                      size="small"
                      onClick={() => toggleMilestoneComplete(index)}
                      color={milestone.completed ? 'success' : 'default'}
                    >
                      {milestone.completed ? <CompleteIcon /> : <MilestoneIcon />}
                    </IconButton>
                    
                    {MILESTONE_ICONS[milestone.type]}
                    
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography variant="body2" fontWeight="bold">
                        {milestone.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {milestone.date} • {milestone.type}
                        {milestone.description && ` • ${milestone.description}`}
                      </Typography>
                    </Box>
                    
                    <IconButton size="small" onClick={() => deleteMilestone(index)} color="error">
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                </Card>
              ))}
            </Stack>
            
            {formData.milestones.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', textAlign: 'center', py: 2 }}>
                No milestones added yet
              </Typography>
            )}
          </Grid>
          
          {/* Actions */}
          <Grid item xs={12}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}
              sx={{ '& > .MuiButton-root': { width: { xs: '100%', sm: 'auto' } } }}>
              <Button
                type="submit"
                variant="contained"
                size="large"
                disabled={!formData.name.trim() || !formData.start_date || !formData.end_date}
              >
                {phase ? t('stepContent.timeline.actions.updatePhase') : t('stepContent.timeline.actions.addPhase')}
              </Button>
              <Button onClick={onCancel} size="large">
                {t('common.cancel')}
              </Button>
            </Stack>
          </Grid>
        </Grid>
      </form>
    </Paper>
  );
};

// Main Timeline Page Component
export default function TimelinePage() {
  const { 
    getStepData, 
    updateStepData, 
    markStepComplete, 
    isStepCompleted 
  } = useRoadmapStore();
  const { t } = useTranslation();
  const handleAISuggest = useAISuggest('timeline');
  const _storeStep = useRoadmapStore(s => s.roadmapData?.steps?.timeline);

  const stepData = getStepData('timeline');
  const [startDate, setStartDate] = useState(stepData.start_date || '');
  const [endDate, setEndDate] = useState(stepData.end_date || '');
  const [phases, setPhases] = useState(stepData.phases || []);
  const [isComplete, setIsComplete] = useState(isStepCompleted('timeline'));
  const [showForm, setShowForm] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);
  const [viewMode, setViewMode] = useState('gantt'); // 'gantt' or 'pert'

  useEffect(() => {
    const data = getStepData('timeline');
    setStartDate(data.start_date || '');
    setEndDate(data.end_date || '');
    setPhases(data.phases || []);
    setIsComplete(isStepCompleted('timeline'));
  }, [getStepData, isStepCompleted, _storeStep]);

  const updateData = () => {
    updateStepData('timeline', {
      start_date: startDate,
      end_date: endDate,
      phases: phases
    });
  };

  const handleSaveDates = () => {
    updateData();
  };

  const handleSavePhase = (phaseData) => {
    const updatedPhases = editingIndex !== null
      ? phases.map((ph, index) => index === editingIndex ? phaseData : ph)
      : [...phases, phaseData];
    setPhases(updatedPhases);
    setShowForm(false);
    setEditingIndex(null);
    updateStepData('timeline', {
      start_date: startDate,
      end_date: endDate,
      phases: updatedPhases
    });
  };

  const handleEditPhase = (index) => {
    setEditingIndex(index);
    setShowForm(true);
  };

  const handleCompleteToggle = () => {
    const newCompleteState = !isComplete;
    const success = markStepComplete('timeline', newCompleteState);
    setIsComplete(success);
  };

  const canComplete = startDate && endDate && phases.length > 0;

  // Calculate statistics
  const stats = useMemo(() => {
    const total = phases.length;
    const completed = phases.filter(p => p.status === 'completed').length;
    const inProgress = phases.filter(p => p.status === 'in-progress').length;
    const delayed = phases.filter(p => p.status === 'delayed').length;
    const critical = phases.filter(p => p.is_critical).length;
    const avgProgress = phases.length > 0
      ? Math.round(phases.reduce((sum, p) => sum + (p.progress || 0), 0) / phases.length)
      : 0;

    return { total, completed, inProgress, delayed, critical, avgProgress };
  }, [phases]);

  return (
    <Box sx={{ maxWidth: 1400, mx: 'auto' }}>
      <StepPageHeader
        stepNum={6}
        title={t(STEP_LABELS.timeline)}
        intro="Plan and visualize your project timeline with Gantt and PERT charts"
        isComplete={isComplete}
        canComplete={canComplete}
        onToggleComplete={handleCompleteToggle}
        requireMessage="Set project dates and add at least one phase to complete this step"
      onAISuggest={handleAISuggest}
        />

      {/* Statistics Dashboard */}
      {phases.length > 0 && (
        <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Timeline Statistics
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={6} sm={4} md={2}>
              <Card variant="outlined" sx={{ textAlign: 'center', p: 2 }}>
                <Typography variant="h4" color="primary">{stats.total}</Typography>
                <Typography variant="caption" color="text.secondary">Total Phases</Typography>
              </Card>
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <Card variant="outlined" sx={{ textAlign: 'center', p: 2 }}>
                <Typography variant="h4" color="success.main">{stats.completed}</Typography>
                <Typography variant="caption" color="text.secondary">Completed</Typography>
              </Card>
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <Card variant="outlined" sx={{ textAlign: 'center', p: 2 }}>
                <Typography variant="h4" color="info.main">{stats.inProgress}</Typography>
                <Typography variant="caption" color="text.secondary">In Progress</Typography>
              </Card>
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <Card variant="outlined" sx={{ textAlign: 'center', p: 2 }}>
                <Typography variant="h4" color="error.main">{stats.delayed}</Typography>
                <Typography variant="caption" color="text.secondary">Delayed</Typography>
              </Card>
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <Card variant="outlined" sx={{ textAlign: 'center', p: 2 }}>
                <Typography variant="h4" color="warning.main">{stats.critical}</Typography>
                <Typography variant="caption" color="text.secondary">Critical</Typography>
              </Card>
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <Card variant="outlined" sx={{ textAlign: 'center', p: 2 }}>
                <Typography variant="h4" color="secondary.main">{stats.avgProgress}%</Typography>
                <Typography variant="caption" color="text.secondary">Avg Progress</Typography>
              </Card>
            </Grid>
          </Grid>
        </Paper>
      )}

      {/* Project Dates */}
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TimelineIcon />
          Project Timeline
        </Typography>
        
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Project Start Date"
              type="date"
              value={startDate}
              onChange={(e) => { setStartDate(e.target.value); updateStepData('timeline', { start_date: e.target.value, end_date: endDate, phases }); }}
              InputLabelProps={{ shrink: true }}
              required
            />
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Project End Date"
              type="date"
              value={endDate}
              onChange={(e) => { setEndDate(e.target.value); updateStepData('timeline', { start_date: startDate, end_date: e.target.value, phases }); }}
              InputLabelProps={{ shrink: true }}
              required
            />
          </Grid>
          
          <Grid item xs={12}>
            <Button
              variant="contained"
              onClick={handleSaveDates}
              disabled={!startDate || !endDate}
            >
              {t('common.save')} Dates
            </Button>
            
            {startDate && endDate && (
              <Chip
                label={`Duration: ${differenceInDays(parseISO(endDate), parseISO(startDate))} days`}
                color="primary"
                sx={{ ml: 2 }}
              />
            )}
          </Grid>
        </Grid>
      </Paper>

      {/* View Toggle and Add Phase */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <ToggleButtonGroup
          value={viewMode}
          exclusive
          onChange={(e, newMode) => newMode && setViewMode(newMode)}
          aria-label="view mode"
        >
          <ToggleButton value="gantt" aria-label="gantt view">
            <GanttIcon sx={{ mr: 1 }} />
            Gantt Chart
          </ToggleButton>
          <ToggleButton value="pert" aria-label="pert view">
            <PertIcon sx={{ mr: 1 }} />
            PERT Diagram
          </ToggleButton>
        </ToggleButtonGroup>

        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => {
            setEditingIndex(null);
            setShowForm(true);
          }}
          disabled={!startDate || !endDate}
        >
          Add Phase
        </Button>
      </Box>

      {/* Phase Form */}
      {showForm && (
        <Box sx={{ mb: 3 }}>
          <PhaseForm
            phase={editingIndex !== null ? phases[editingIndex] : null}
            phaseIndex={editingIndex !== null ? editingIndex : phases.length}
            phases={phases}
            onSave={handleSavePhase}
            onCancel={() => {
              setShowForm(false);
              setEditingIndex(null);
            }}
          />
        </Box>
      )}

      {/* Visualization */}
      {phases.length > 0 ? (
        <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
          {viewMode === 'gantt' ? (
            <GanttChart
              phases={phases}
              startDate={startDate}
              endDate={endDate}
              onPhaseClick={handleEditPhase}
            />
          ) : (
            <PertChart phases={phases} />
          )}
        </Paper>
      ) : !showForm && (
        <Paper elevation={2} sx={{ p: 6, textAlign: 'center' }}>
          <TimelineIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No Phases Yet
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {startDate && endDate 
              ? 'Add your first phase to start building the timeline'
              : 'Set project start and end dates first, then add phases'}
          </Typography>
          {startDate && endDate && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setShowForm(true)}
            >
              Add First Phase
            </Button>
          )}
        </Paper>
      )}
    </Box>
  );
}
